import React, { useContext, useState, useEffect } from "react";
import { buyNowContext, shippingMethods, recipientType } from "../BuyNow";
import CountriesPropTypes from "../../prop-types/CountriesPropTypes";
import ShapaPayPalButton from "../../utils/ShapaPayPalButton";
import mailChimpApi, { MAIL_CHIMP_LIST_IDS, MAIL_CHIMP_TAGS, MAIL_CHIMP_TAGS_STATUS } from "../../../../api/mailChimpApi";
import trackingScripts from "../../utils/trackingScripts";
import { createOrderObject, FACEBOOK_EVENTS } from "../../utils/facebookHelper";
import GiftForm from "./GiftForm";
import { PORTALS, PRODUCT_CATEGORIES, PURCHASE_FLOWS } from "../../../../constants";
import { isPayPalSupported } from "../../utils/orderHelper";
import usersApi from "../../../../api/usersApi";
import addressValidationApi from "../../../../api/addressValidationApi";
import { useSimpleReactValidator } from "../../utils/useSimpleReactValidator";
import npiValidationApi from "../../../../api/npiValidationApi";
import organizationsApi from "../../../../api/organizationsApi";

const ShippingStage = ({ countries }) => {
  const validator = useSimpleReactValidator();
  const {
    PURCHASE_STAGES, shippingStageState, setShippingStageState, lineItems, setOrder, setPurchaseStage,
    isProduction, promoCode, referralCode, salesTaxRates, giftSenderInformation, setGiftSenderInformation,
    portal, purchaseFlow, quantitiesEnabled, appInfo,
    adminReferralTargetCode, npi, setNpi, deferredUserCreate, setDeferredUserCreate, CLINICIAN_MODES, clinicianMode
  } = useContext(buyNowContext);

  const [emailDomainValidationError, setEmailDomainValidationError] = useState('');

  const deviceLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.SCALE);
  const planLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.MEMBERSHIP);
  const adminAccessLineItem = lineItems.find(li => li.product.category === PRODUCT_CATEGORIES.ADMIN_ACCESS);
  const selectedDevice = deviceLineItem ? deviceLineItem.product : null;
  const selectedPlan = planLineItem ? planLineItem.product : null;

  // We offer gifting for orders including a scale product in the Shapa portal.
  const giftingEnabled = portal === PORTALS.SHAPA && deviceLineItem;

  // If the user is purchasing an admin access product, we need them to create an account.
  const createAccountEnabled = !!adminAccessLineItem && clinicianMode !== CLINICIAN_MODES.EXISTING_ORGANIZATION;

  const { country } = shippingStageState;
  const [errorMessage, setErrorMessage] = useState(null);
  const [paypalErrorMessage, setPaypalErrorMessage] = useState(null);
  const fbTrackingOrderObject = createOrderObject(selectedDevice, selectedPlan);

  const isClinician = purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_CLINICIAN || purchaseFlow === PURCHASE_FLOWS.SHAPA_HEALTH_CLINICIAN;

  let createAccountInstructions;
  if (purchaseFlow === PURCHASE_FLOWS.CLEARSTEP_PARENT) {
    createAccountInstructions = `You'll use the same email and password below to log in to the Parent Portal to access your child's data${selectedPlan ? ' as well as to manage your billing account' : ''}.`;
  } else {
    createAccountInstructions = `You'll use the same email and password below to log into the ${appInfo.name} portal. Text verification codes will be sent to your phone number to add an extra layer of security to your account.`;
  }

  useEffect(() => {
    trackingScripts.hotjar(isProduction, "/orders/buy-now/shipping_stage");
    trackingScripts.facebook(isProduction, FACEBOOK_EVENTS.REACT_PAGE_VIEW, { page: "ShippingStage" }, "trackCustom");
    trackingScripts.facebook(isProduction, FACEBOOK_EVENTS.ADD_TO_CART, fbTrackingOrderObject);
    trackingScripts.sendGoogleAnalyticsEvent(isProduction, "add_to_cart", "ecommerce", "Add To Cart");
  }, []);

  /**
 * Validate that the email is correct.
 * This can be called when the email input field loses focus.
 * @param {string} email - The email address to be validated.
 * @param {(error: string) => void} setEmailValidationError - Function to set the email validation error message.
 * @param {() => void} onSuccess - Function to be called when the email validation is successful.
 * @return {Promise<void>}
 */
  const handleEmailValidation = async (email, setEmailValidationError, onSuccess) => {
    setEmailValidationError('');

    // If the user hasn't entered an email address, let the default validation handle it.
    if (!email?.length)
      return;
    try {
      const response = await usersApi.validateEmail(email);
      if (response.isValid) {
        onSuccess();
      } else {
        setEmailValidationError("Email address not recognized. Please ensure the email is correct and that there are no typos.");
      }
    } catch (error) {
      setEmailValidationError("Email address not recognized. Please ensure the email is correct and that there are no typos.");
    }
  };

  /**
   * Validate that the shipping state is correct.
   * This can be called before proceeding in the purchase flow.
   * @param {"paypal"|"stripe"} paymentProcessor
   * @return {Promise<Boolean>}
   */
  const validate = async paymentProcessor => {
    setErrorMessage(null);
    setPaypalErrorMessage(null);

    // First, reset the error state. The state will be repopulated when we execute validation.
    validator.current.hideMessages();
    setShippingStageState({ ...shippingStageState });
    setGiftSenderInformation({ ...giftSenderInformation });

    // Stripe does not support all of the countries that we ship to.
    if (paymentProcessor === "stripe" && !country.supportsStripe) {
      setErrorMessage("Sorry, we don't accept credit card payments from your country. Please use PayPal to place your order.");
      return false;
    }

    if (validator.current.allValid()) {
      if (createAccountEnabled) {
        if (npi && npi.trim().length) {
          try {
            await npiValidationApi.validateNpi(npi);
          } catch (response) {
            setErrorMessage(`NPI validation issue: ${response.error}`);
            return false;
          }
        }

        try {
          await usersApi.validateNewParams({...shippingStageState}, deferredUserCreate);
        }
        catch (response) {
          setErrorMessage(`There was an issue creating your account: ${response.error}`);
          return false;
        }
      }

      if (clinicianMode === CLINICIAN_MODES.EXISTING_ORGANIZATION) {
        let result = false;

        try {
          result = await organizationsApi.validateOrganizationCode(shippingStageState.organizationCode);
        } catch (response) { }

        if (!result) {
          setErrorMessage("The organization code you have entered is not valid.");
          return false;
        }
      }

      return true;
    }

    validator.current.showMessages();
    setShippingStageState({ ...shippingStageState });
    setGiftSenderInformation({ ...giftSenderInformation });
    return false;
  };

  const validateAddress = async () => {
    if (shippingStageState.country.code !== "US") {
      // we only interested in validating US addresses
      return true;
    }
    setErrorMessage(null);
    setPaypalErrorMessage(null);

    // First, reset the error state. The state will be repopulated when we execute validation.
    validator.current.hideMessages();

    try {
      const { validatedAddress } = await addressValidationApi.validateAddress({
        name: `${shippingStageState.firstName} ${shippingStageState.lastName}`,
        phone: shippingStageState.phone,
        addressLine1: shippingStageState.address1,
        addressLine2: shippingStageState.address2,
        cityLocality: shippingStageState.city,
        stateProvince: shippingStageState.state,
        postalCode: shippingStageState.zipCode,
        countryCode: shippingStageState.country.code,
      });

      if (validatedAddress.status === "verified") {
        return true;
      } else {
        setErrorMessage("There was an issue verifying your address. Please ensure your address information is correct and try again.");
        return false;
      }
    } catch (_) {}
    // Do not stop the order process because of the address validation.
    return true;
  };

  /**
   * Applicable for Stripe orders only. This method will ensure all fields are filled in,
   * and will then proceed to the 'checkout' stage where the users will enter their payment info.
   */
  const continueToCheckoutStage = async () => {
    const validationResult = await validate("stripe");
    if (validationResult) {
      const addressValidationResult = await validateAddress();
      if (addressValidationResult) {
        trackingScripts.facebook(isProduction, FACEBOOK_EVENTS.CREDIT_CARD_PAYMENT, fbTrackingOrderObject, "trackCustom", shippingStageState.email);
        setPurchaseStage(PURCHASE_STAGES.PAYMENT);
      }
    }
  };

  // Grab the shipping prices for the selected country into some readable variable names.
  const { standard: standardShippingPrice, expedited: expeditedShippingPrice } = country.shippingPrices;

  /**
   * Upon a successful PayPal payment, the response will be a JSON representation of an order.
   * @param {Object} response
   */
  const onPayPalSuccess = (response) => {
    // When the customer has successfully made a purchase, remove them from Mailchimp as a potential customer.
    if (purchaseFlow === PURCHASE_FLOWS.SHAPA_END_USER) {
      mailChimpApi.updateUserTag(shippingStageState.email, MAIL_CHIMP_LIST_IDS.SHAPA_SUBSCRIBERS_LIST, MAIL_CHIMP_TAGS.ABANDONED_CART_TAG, MAIL_CHIMP_TAGS_STATUS.INACTIVE);
    }

    trackingScripts.facebook(isProduction, FACEBOOK_EVENTS.PAYPAL_PAYMENT, fbTrackingOrderObject, "trackCustom", shippingStageState.email);
    setOrder(response);
    setPurchaseStage(PURCHASE_STAGES.CONFIRMATION);
  };

  /**
   * Called when an error occurs during the PayPal checkout process.
   * @param {String} errorMessage
   */
  const onPayPalError = errorMessage => {
    setPaypalErrorMessage(`There was an error submitting your PayPal order: ${errorMessage}`);
  };

  const onPayPalValidation = async () => {
    const validationResult = await validate("paypal");
    if (validationResult) {
      return await validateAddress();
    }
    return validationResult;
  };

  const giftCheckboxHandle = (event) => {
    const giftInitialData = { name: "", email: "", message: "", giftConfirmationEmail: recipientType.me, isGift: false };
    const giftStateUpdate = { ...giftSenderInformation, isGift: event.target.checked };
    if (event.target.checked) {
      setGiftSenderInformation(giftStateUpdate);
    } else {
      setGiftSenderInformation(giftInitialData);
    }

    /**
     * Hide validator messages when the user selects the `This is a gift` option.
     * Current validator messages may or may not be applicable.
     */
    validator.current.hideMessages();
  };

  /**
   * The email field is shared between the shipping/billing information and account creation.
   * @returns {JSX.Element}
   */
  const emailField = () => (
    <React.Fragment>
      <input
        type="email"
        id="shippingInputEmail"
        name='email'
        className="form-control form-control-inputs"
        placeholder="Email"
        value={shippingStageState.email}
        onChange={e => {
          setShippingStageState({...shippingStageState, email: e.target.value})
        }}
        onBlur={() => handleEmailValidation(shippingStageState.email, setEmailDomainValidationError, () => {
          if (purchaseFlow === PURCHASE_FLOWS.SHAPA_END_USER) {
            mailChimpApi.saveEmailAddressToList(shippingStageState.email, MAIL_CHIMP_LIST_IDS.SHAPA_SUBSCRIBERS_LIST, MAIL_CHIMP_TAGS.ABANDONED_CART_TAG)
          }
        })}
        
      />
      {validator.current.message('email', shippingStageState.email, 'required|email')}
      {emailDomainValidationError && <div className="text-danger plan-main-txt">{emailDomainValidationError}</div>}
    </React.Fragment>
  );

  /**
   * The phone field is shared between the shipping/billing information and account creation.
   * @param {Boolean} [required = false]
   * @returns {JSX.Element}
   */
  const phoneField = (required = false) => (
    <React.Fragment>
      <input
        type="text"
        id="shippingInputPhone"
        name={'phone'}
        className="form-control form-control-inputs"
        placeholder="Phone Number"
        value={shippingStageState.phone}
        onChange={e => {
          setShippingStageState({...shippingStageState, phone: e.target.value})
        }}
      />
      {required &&
        <React.Fragment>
          {validator.current.message('phone', shippingStageState.phone, 'required|phone')}
        </React.Fragment>
      }
    </React.Fragment>
  );

  const npiField = () => (
    <React.Fragment>
      <input
        type="text"
        id="shippingInputNpi"
        name={'npi'}
        className="form-control form-control-inputs"
        placeholder="NPI Number"
        defaultValue={npi}
        onChange={e => {
          setNpi(e.target.value)
        }}
        minlength={10}
        maxlength={10}
      />
      {validator.current.message('npi', shippingStageState.npi, 'numeric|min:10|max:10')}
    </React.Fragment>
  );

  // Since some fields are conditional, we need to purge the validator fields every render to make sure they are up-to-date for the current state.
  // (Ex. state is conditional depending on the selected country, gift validations depending on whether the user is purchasing as a gift).
  // See: https://github.com/dockwa/simple-react-validator#3-conditional-fields
  validator.current.purgeFields();

  return (
    <div className="col-12 offset-lg-1 col-lg-6 mt-4 mt-lg-0">
      {/* It's possible a scale won't have a shipping option (ex. there is a delay so we don't offer expedited shipping). */}
      {selectedDevice && selectedDevice.shippingOptionsAvailable &&
        <div className="col-lg-11 d-flex flex-column me-0 px-0">
          <p className="header-txt text-dark mb-0 pb-3 mt-3 mt-lg-0">Shipping method</p>
          <div className="col-lg-6 form-check d-flex align-items-start checkout-check-container d-flex me-0 pe-0">
            <input
              type="radio"
              checked={shippingStageState.shippingMethod === shippingMethods.standard}
              id="regular-shipping"
              name="shippingMethod"
              className="col-1 form-check-input form-check-input-radio me-2"
              onChange={() => setShippingStageState({...shippingStageState, shippingMethod: shippingMethods.standard})}
            />
            <div className="col-11 d-flex justify-content-between">
              <label className="form-check-label" htmlFor="regular-shipping">
                <p className="plan-main-txt fw-bolder text-dark pb-0 mb-0">Regular shipping</p>
                <p className="sub-txt text-muted">({shippingMethods.standard.timeFrame})</p>
              </label>
              <p className="plan-main-txt fw-bold text-dark">{`$${standardShippingPrice.toFixed(2)}${quantitiesEnabled ? "/item" : ""}`}</p>
            </div>
          </div>
          {
            // Expedited shipping is only available for domestic orders.
            country.code === 'US' &&
            <div className="col-lg-6 form-check d-flex me-0 pe-0">
              <input
                type="radio"
                id="expedited-shipping"
                name="shippingMethod"
                checked={shippingStageState.shippingMethod === shippingMethods.expedited}
                className="col-1 form-check-input form-check-input-radio me-2"
                onChange={() => setShippingStageState({
                  ...shippingStageState,
                  shippingMethod: shippingMethods.expedited
                })}
              />
              <div className="col-11 d-flex justify-content-between">
                <label className="form-check-label" htmlFor="expedited-shipping">
                  <p className="plan-main-txt fw-bolder text-dark pb-0 mb-0">Expedited shipping</p>
                  <p className="sub-txt text-muted">({shippingMethods.expedited.timeFrame})</p>
                </label>
                <p className="plan-main-txt fw-bold text-dark">{`$${expeditedShippingPrice.toFixed(2)}${quantitiesEnabled ? "/item" : ""}`}</p>
              </div>
            </div>
          }
        </div>
      }

      <div className="col-lg-11 px-0">
        {/* If the user is not purchasing a scale, we'll use their shipping information as billing information. */}
        <p className="header-txt mt-3 text-dark mb-0 ">{deviceLineItem ? 'Shipping information' : 'Billing information'}</p>

        {giftingEnabled && <div className={`form-check d-lg-flex align-items-lg-center px-4 ${giftSenderInformation.isGift ? 'pb-2' : 'pb-4'}`}>
            <input
              id="giftCheck"
              name="checkboxGift"
              className="form-check-input me-2"
              type="checkbox"
              checked={giftSenderInformation.isGift}
              onChange={giftCheckboxHandle}
            />
            <label className="form-check-label plan-main-txt fw-bolder pt-lg-1" htmlFor="giftCheck">
              This is a gift
            </label>
          </div>
        }

        <form className="row g-2 p-0">
          <GiftForm validator={validator.current}/>

          {/* This text is only helpful for clarification in a flow where gifting is a possibility. */}
          {giftingEnabled && <p className="plan-main-txt fw-bolder text-dark pb-0">Recipient shipping information</p>}

          <div className="col-12">
            <div className="row g-2 p-0">
              <div className="col-12 col-md-6">
                <input
                  type="text"
                  id="firstName"
                  className="form-control form-control-inputs"
                  placeholder="First name"
                  value={shippingStageState.firstName}
                  onChange={e => {
                    setShippingStageState({...shippingStageState, firstName: e.target.value})
                  }}
                />
                {validator.current.message('firstName', shippingStageState.firstName, 'required')}
              </div>

              <div className="col-12 col-md-6">
                <input
                  type="text"
                  id="shippingInputLastName"
                  name={'lastName'}
                  className="form-control form-control-inputs"
                  placeholder="Last name"
                  value={shippingStageState.lastName}
                  onChange={e => {
                    setShippingStageState({...shippingStageState, lastName: e.target.value})
                  }}
                />
                {validator.current.message('lastName', shippingStageState.lastName, 'required')}
              </div>
            </div>
          </div>

          {/* If account creation is enabled, we'll ask for the email there. */}
          {!createAccountEnabled &&
            <div className="col-12">
              {emailField()}
            </div>
          }

          <div className="col-12">
            <input
              type="text"
              id="shippingInputAddress"
              name={'address1'}
              className="form-control form-control-inputs"
              placeholder="Address 1"
              value={shippingStageState.address1}
              onChange={e => {
                setShippingStageState({...shippingStageState, address1: e.target.value})
              }}
            />
            {validator.current.message('address1', shippingStageState.address1, 'required')}
          </div>

          <div className="col-12">
            <input
              type="text"
              id="shippingInputAddress2"
              name={'address2'}
              className="form-control form-control-inputs"
              placeholder="Address 2"
              value={shippingStageState.address2}
              onChange={e => {
                setShippingStageState({...shippingStageState, address2: e.target.value})
              }}
            />
          </div>

          <div className="col-12">
            <select
              id="shippingInputCountry"
              name={'country'}
              className="form-control form-control-inputs form-select"
              placeholder="Country"
              value={shippingStageState.country.code}
              onChange={e => {
                const countryCode = e.target.value;

                setShippingStageState({
                  ...shippingStageState,
                  country: countries[countryCode],
                  shippingMethod: shippingMethods.standard
                });
              }}
            >
              {Object.keys(countries).map(countryCode => {
                const country = countries[countryCode];
                return (
                  <option key={countryCode} value={countryCode}>{country.name}</option>
                );
              })}
            </select>
            {validator.current.message('country', shippingStageState.country, 'required')}
          </div>

          <div className="col-12">
            <div className="row g-2 p-0">
              <div className="col-md-4">
                <input
                  type="text"
                  id="shippingInputCity"
                  name={'city'}
                  className="form-control form-control-inputs"
                  placeholder="City"
                  value={shippingStageState.city}
                  onChange={e => {
                    setShippingStageState({...shippingStageState, city: e.target.value})
                  }}
                />
                {validator.current.message('city', shippingStageState.city, 'required')}
              </div>

              <div className="col-md-4">
                <input
                  type="text"
                  id="shippingInputState"
                  name={'state'}
                  className="form-control form-control-inputs"
                  placeholder={country.code === "US" ? "State Code" : "State"}
                  value={shippingStageState.state}
                  maxLength={country.code === "US" ? 2 : 50}
                  onChange={e => {
                    setShippingStageState({...shippingStageState, state: e.target.value})
                  }}
                />
                {country.requiresState ? validator.current.message('state', shippingStageState.state, 'required') : null}
              </div>

              <div className="col-md-4">
                <input
                  type="text"
                  id="shippingInputZip"
                  name={'zipCode'}
                  className="form-control form-control-inputs"
                  placeholder="Zip Code"
                  value={shippingStageState.zipCode}
                  onChange={e => {
                    setShippingStageState({...shippingStageState, zipCode: e.target.value})
                  }}
                />
                {country.requiresZip ? validator.current.message('zipCode', shippingStageState.zipCode, 'required') : null}
              </div>
            </div>
          </div>

          {/* If account creation is enabled, we'll ask for the phone number there. */}
          {!createAccountEnabled &&
            <div className="col-12">
              {phoneField()}
            </div>
          }

          {isClinician && clinicianMode === CLINICIAN_MODES.EXISTING_ORGANIZATION &&
            <React.Fragment>
              <p className="header-txt mt-3 text-dark pb-0 mt-4">Enter your organization code</p>

              <p className="plan-main-txt text-dark pb-0">Your organization code can be found in the 'Organizations' page of the admin portal.</p>

              <div className="col-12">
                <div className="row g-2 p-0">
                  <div className="col-12">
                    <input
                      type="text"
                      className="form-control form-control-inputs"
                      placeholder="Organization Code"
                      value={shippingStageState.organizationCode}
                      onChange={e => {
                        setShippingStageState({...shippingStageState, organizationCode: e.target.value})
                      }}
                    />
                    {validator.current.message('organizationCode', shippingStageState.organizationCode, 'required')}
                  </div>
                </div>
              </div>
            </React.Fragment>
          }

          {createAccountEnabled &&
            <React.Fragment>
              <p className="header-txt mt-3 text-dark pb-0 mt-4">Create an account</p>
              <p className="plan-main-txt text-dark pb-0">{createAccountInstructions}</p>

              <div className="col-12">
                <div className="row g-2 p-0">
                  <div className="col-12 col-md-6">
                    {emailField()}
                  </div>

                  <div className="col-md-6">
                    {phoneField(isClinician)}
                  </div>
                </div>
              </div>

              {isClinician &&
                <div className="col-12">
                  {npiField()}
                </div>
              }

              {(!deferredUserCreate || !isClinician) &&
                <div className="col-12">
                  <div className="row g-2 p-0">
                    <div className="col-12 col-md-6">
                      <input
                        type="password"
                        id="shippingInputPassword"
                        name={'password'}
                        className="form-control form-control-inputs"
                        placeholder="Password"
                        value={shippingStageState.password}
                        onChange={e => {
                          setShippingStageState({...shippingStageState, password: e.target.value})
                        }}
                      />
                      {validator.current.message('password', shippingStageState.password, 'required')}
                    </div>

                    <div className="col-12 col-md-6">
                      <input
                        type="password"
                        id="shippingInputPasswordConfirmation"
                        name={'passwordConfirmation'}
                        className="form-control form-control-inputs"
                        placeholder="Confirm password"
                        value={shippingStageState.passwordConfirmation}
                        onChange={e => {
                          setShippingStageState({...shippingStageState, passwordConfirmation: e.target.value})
                        }}
                      />
                      {validator.current.message('passwordConfirmation', shippingStageState.passwordConfirmation, 'required')}
                    </div>
                  </div>
                </div>
              }

              {/* For the clinician flow, we want an extra field - the clinic/practice name. */}
              {isClinician &&
                <div className="col-12">
                  <input
                    type="text"
                    id="shippingInputOrganizationName"
                    name={'organizationName'}
                    className="form-control form-control-inputs"
                    placeholder="Clinic or practice name"
                    value={shippingStageState.organizationName}
                    onChange={e => {
                      setShippingStageState({...shippingStageState, organizationName: e.target.value});
                    }}
                  />
                  {validator.current.message('organizationName', shippingStageState.organizationName, 'required')}
                </div>
              }

              {/* Clinicians have the option of purchasing on behalf of the organization owner. */}
              {isClinician &&
                <>
                <div className="form-check d-lg-flex align-items-lg-center col-10 offset-1 px-4 col-lg-12 offset-lg-0 pt-3">
                  <input
                    id="alreadyRegisteredClinician"
                    type="checkbox" className="form-check-input me-2"
                    defaultChecked={false}
                    onClick={(event) => { setDeferredUserCreate(event.target.checked) }}
                  />
                  <label className="form-check-label plan-main-txt fw-bolder pt-lg-1" htmlFor="alreadyRegisteredClinician">
                    {"I am an employee purchasing on behalf of the organization owner."}
                  </label>
                </div>

                {deferredUserCreate &&
                  <>
                    <span className="form-check-label plan-main-txt fw-bold pt-lg-1">{"Please enter the organization owner's email and phone number above. They will be emailed important information to proceed with account creation."}</span>
                  </>
                }

                </>
              }
            </React.Fragment>
          }
        </form>
      </div>

      <div>
        {errorMessage &&
          <p className="plan-main-txt text-danger text-center">
            {errorMessage}
          </p>
        }

        {paypalErrorMessage && !errorMessage &&
          <p className="plan-main-txt text-danger text-center">
            {paypalErrorMessage}
          </p>
        }
      </div>

      <div className="row m-0 mt-lg-3 justify-content-center col-lg-11 action-btn-container">
        {/* At this time, we don't support PayPal for orders where the quantity is > 1 */}
        {isPayPalSupported(lineItems) &&
          <div className="col-lg-6 px-lg-0 paypal-btn-container">
            <ShapaPayPalButton
              onSuccess={onPayPalSuccess}
              onError={onPayPalError}
              validate={onPayPalValidation}
              shippingStageState={shippingStageState}
              lineItems={lineItems}
              promoCode={promoCode}
              referralCode={referralCode}
              salesTaxRates={salesTaxRates}
              isProduction={isProduction}
              giftSenderInformation={giftSenderInformation}
              portal={portal}
              purchaseFlow={purchaseFlow}
              adminReferralTargetCode={adminReferralTargetCode}
              npi={npi}
              deferredUserCreate={deferredUserCreate}
            />
          </div>
        }
        <div className="col-lg-6 d-flex justify-content-center p-lg-0">
          <a
            className="teal button-link next-button-txt next-step-button align-self-start shipping-info-btn"
            onClick={continueToCheckoutStage}
          >
            Next: Payment information
          </a>
        </div>
        <div className="col-lg-8 d-none d-lg-inline text-center">
          <a
            className="sub-txt text-center align-bottom fw-bolder border-bottom-solid text-dark back-link"
            onClick={() => { setPurchaseStage(PURCHASE_STAGES.PRODUCT_SELECTION); }}
          >
            Return to products and plan
          </a>
        </div>
      </div>
    </div>
  );
};

ShippingStage.propTypes = {
  countries: CountriesPropTypes.isRequired
};

export default ShippingStage;
